import * as THREE from 'three';

// ============================================================
// Vibe Jam Portal — glowing torus with particles and label
// ============================================================

const PORTAL_RADIUS = 15;
const TUBE_RADIUS = 2;
const PARTICLE_COUNT = 800;
const COLLISION_DISTANCE = 25;

export class Portal {
  readonly group: THREE.Group;
  private readonly particles: THREE.BufferGeometry;
  private readonly innerMaterial: THREE.MeshBasicMaterial;
  private readonly label: THREE.Mesh;

  constructor(
    scene: THREE.Scene,
    position: THREE.Vector3,
    color: number,
    labelText: string,
  ) {
    this.group = new THREE.Group();
    this.group.position.copy(position);
    // Tilt slightly so it faces the player better
    this.group.rotation.x = 0.35;

    // Torus ring
    const torusGeom = new THREE.TorusGeometry(PORTAL_RADIUS, TUBE_RADIUS, 16, 100);
    const torusMat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.8,
    });
    this.group.add(new THREE.Mesh(torusGeom, torusMat));

    // Inner disc
    const innerGeom = new THREE.CircleGeometry(PORTAL_RADIUS - 2, 32);
    this.innerMaterial = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.3,
      side: THREE.DoubleSide,
    });
    this.group.add(new THREE.Mesh(innerGeom, this.innerMaterial));

    // Glow light
    const light = new THREE.PointLight(color, 2, 100);
    light.position.set(0, 0, 5);
    this.group.add(light);

    // Particles around the ring
    this.particles = new THREE.BufferGeometry();
    const positions = new Float32Array(PARTICLE_COUNT * 3);
    const colors = new Float32Array(PARTICLE_COUNT * 3);
    const baseColor = new THREE.Color(color);

    for (let i = 0; i < PARTICLE_COUNT * 3; i += 3) {
      const angle = Math.random() * Math.PI * 2;
      const radius = PORTAL_RADIUS + (Math.random() - 0.5) * 4;
      positions[i] = Math.cos(angle) * radius;
      positions[i + 1] = Math.sin(angle) * radius;
      positions[i + 2] = (Math.random() - 0.5) * 4;
      colors[i] = baseColor.r * (0.8 + Math.random() * 0.2);
      colors[i + 1] = baseColor.g * (0.8 + Math.random() * 0.2);
      colors[i + 2] = baseColor.b * (0.8 + Math.random() * 0.2);
    }

    this.particles.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.particles.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const particleMat = new THREE.PointsMaterial({
      size: 0.3,
      vertexColors: true,
      transparent: true,
      opacity: 0.6,
    });
    this.group.add(new THREE.Points(this.particles, particleMat));

    // Label above portal
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    canvas.width = 512;
    canvas.height = 64;
    const hex = `#${color.toString(16).padStart(6, '0')}`;
    ctx.fillStyle = hex;
    ctx.font = 'bold 28px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(labelText, canvas.width / 2, canvas.height / 2 + 10);

    const texture = new THREE.CanvasTexture(canvas);
    const labelGeom = new THREE.PlaneGeometry(30, 5);
    const labelMat = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      side: THREE.DoubleSide,
      depthTest: false,
    });
    this.label = new THREE.Mesh(labelGeom, labelMat);
    this.label.position.y = PORTAL_RADIUS + 5;
    this.label.renderOrder = 999;
    this.group.add(this.label);

    scene.add(this.group);
  }

  update(): void {
    // Animate particles
    const positions = this.particles.attributes.position.array as Float32Array;
    const t = Date.now() * 0.001;
    for (let i = 0; i < positions.length; i += 3) {
      positions[i + 1] += 0.05 * Math.sin(t + i);
    }
    this.particles.attributes.position.needsUpdate = true;

    // Pulse inner opacity
    this.innerMaterial.opacity = 0.25 + 0.1 * Math.sin(t * 2);

    // Label always faces camera (billboarding done by Three.js for sprites, but mesh needs manual)
    this.label.lookAt(
      this.label.position.x + 0,
      this.label.position.y + 100,
      this.label.position.z + 100,
    );
  }

  checkCollision(playerPos: THREE.Vector3): boolean {
    return playerPos.distanceTo(this.group.position) < COLLISION_DISTANCE;
  }

  dispose(scene: THREE.Scene): void {
    scene.remove(this.group);
  }
}

// ============================================================
// Portal URL helpers
// ============================================================

export function getPortalParams(): URLSearchParams {
  return new URLSearchParams(window.location.search);
}

export function isFromPortal(): boolean {
  return getPortalParams().get('portal') === 'true';
}

export function getRefUrl(): string | null {
  return getPortalParams().get('ref');
}

export function getPortalUsername(): string | null {
  return getPortalParams().get('username');
}

export function buildExitUrl(username: string, gameUrl: string): string {
  const params = new URLSearchParams();
  params.set('portal', 'true');
  params.set('username', username);
  params.set('color', 'red');
  params.set('speed', '80');
  params.set('ref', gameUrl);
  return `https://jam.pieter.com/portal/2026?${params.toString()}`;
}

export function buildReturnUrl(refUrl: string): string {
  const currentParams = getPortalParams();
  const newParams = new URLSearchParams();
  for (const [key, value] of currentParams) {
    if (key !== 'ref') {
      newParams.set(key, value);
    }
  }
  let url = refUrl;
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = 'https://' + url;
  }
  const paramString = newParams.toString();
  return url + (paramString ? '?' + paramString : '');
}
